/**
 * Format a product's metadata bag as a compact subtitle string.
 *
 * `product_metadata` is an unstructured JSON bag (`Record<string, any>`) whose
 * values are expected to be flat scalars (the metadata editor only writes
 * `string | number | boolean`). ERP sync and seeds, however, also store nested
 * objects under keys like `rhs` / `family` / `shipper` / `receiver`. Those must
 * never surface here — `String({})` is `"[object Object]"`, which would leak
 * under the product name in the picker.
 *
 * So only primitive, finite, non-blank values are kept; nested objects/arrays
 * (and `NaN`, which is `typeof "number"` but would render as `"NaN"`) are
 * skipped. This is the single source of truth shared by every product picker.
 */
export function formatProductMetadata(
  metadata: Record<string, any> | null | undefined,
): string | null {
  if (!metadata) return null;
  const vals = Object.values(metadata)
    .filter(
      (v) =>
        typeof v === "string" ||
        (typeof v === "number" && Number.isFinite(v)) ||
        typeof v === "boolean",
    )
    .map(String)
    .filter((s) => s.trim() !== "");
  return vals.length > 0 ? vals.join(", ") : null;
}
